import type { FilterRuleModel, ScenarioModel } from '@/shared/generated'
import type { JSONSchemaType } from 'ajv'
import type { UseFormReturn } from 'react-hook-form'
import type { ScenarioFormData } from '../model/types'

import React, { createContext, use, useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useSettingsSchemasQuery } from '@/entities/core'
import {
	useScenarioQuery,
	useUpdateScenarioMutation,
} from '@/entities/scenario'

import { ampli } from '@/shared/lib/ampli'
import { ajvResolver } from '@/shared/lib/validator/ajv'
import { getResolvedSchema } from '@/shared/tools/json-schema-utils'

type ScenarioItemContextType = {
	scenario?: ScenarioModel
	skuRules: FilterRuleModel[]
	schema?: JSONSchemaType<ScenarioFormData>
	methods: UseFormReturn<ScenarioFormData>
	isLoading: boolean
	isPending: boolean
	canManage?: boolean
	handleSubmit: () => void
	handleReset: () => void
}
export const ScenarioItemContext = createContext<ScenarioItemContextType>({
	skuRules: [],
	methods: {} as UseFormReturn<ScenarioFormData>,
	isLoading: false,
	isPending: false,
	handleSubmit: () => {},
	handleReset: () => {},
})

export const ScenarioItemProvider = ({
	scenarioId,
	children,
}: {
	children: React.ReactNode
	scenarioId: string
}) => {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const scenarioQuery = useScenarioQuery(scenarioId, {
		enabled: !!scenarioId,
	})
	const schemaQuery = useSettingsSchemasQuery({
		select: getResolvedSchema(`entity:scenario`),
	})
	const updateScenarioMutation = useUpdateScenarioMutation(scenarioId, {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['scenarios'],
			})
			queryClient.invalidateQueries({
				queryKey: ['scenarios-query-list'],
			})
		},
	})
	const methods = useForm<ScenarioFormData, any, ScenarioFormData>({
		defaultValues: {
			description: '',
		},
		values: scenarioQuery.data
			? {
					name: scenarioQuery.data.name,
					description: scenarioQuery.data.description! || '',
					settings: scenarioQuery.data.settings,
				}
			: undefined,
		resolver: ajvResolver(schemaQuery.data!, { strict: false }),
	})
	const handleSubmit = methods.handleSubmit((data: ScenarioFormData) => {
		ampli.scenarioChangesSaved()
		toast.promise(updateScenarioMutation.mutateAsync(data), {
			loading: t('saving.proccess', { defaultValue: 'Saving...' }),
			success: t('saving.success', { defaultValue: 'Saved successfully' }),
			error: t('saving.error', { defaultValue: 'Error while saving' }),
		})
	})
	const handleReset = useCallback(() => {
		methods.reset()
	}, [methods])
	const context = useMemo(
		() => ({
			skuRules: [],
			scenario: scenarioQuery.data,
			schema: schemaQuery.data,
			methods,
			isLoading: scenarioQuery.isLoading,
			isPending: updateScenarioMutation.isPending,
			handleSubmit,
			handleReset,
		}),
		[
			methods,
			scenarioQuery,
			schemaQuery.data,
			handleSubmit,
			handleReset,
			updateScenarioMutation,
		]
	)

	return <ScenarioItemContext value={context}>{children}</ScenarioItemContext>
}

export const useScenarioItemContext = () => use(ScenarioItemContext)
